import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Bot,
  Blocks,
  CalendarRange,
  ChevronDown,
  Menu,
  ChevronRight,
  CreditCard,
  Gamepad2,
  Gift,
  LayoutDashboard,
  MessageSquare,
  PackageSearch,
  Palette,
  Server,
  Settings,
  Shield,
  Sparkles,
  Users,
} from 'lucide-react'

import { usePendingMembers } from '@/hooks/useQQBotQuery'
import { cn } from '@/lib/utils'
import { resolveMediaUrl } from '@/lib/media'
import { useAuthStore } from '@/stores/authStore'
import {
  BottomSheet,
  BottomSheetContent,
  BottomSheetHandle,
  BottomSheetHeader,
  BottomSheetTitle,
} from '@/components/ui/mobile-bottom-sheet'

interface MenuItem {
  path: string
  label: string
  icon: React.ReactNode
}

interface MenuGroup {
  id: string
  label: string
  items: MenuItem[]
  defaultExpanded?: boolean
}

interface AdminSidebarProps {
  className?: string
}

export const adminCoreMenuItems: MenuItem[] = [
  {
    path: '/admin',
    label: '管理概览',
    icon: <LayoutDashboard className="h-5 w-5" />,
  },
  {
    path: '/admin/servers',
    label: '代理节点',
    icon: <Server className="h-5 w-5" />,
  },
  {
    path: '/admin/bots',
    label: '机器人管理',
    icon: <Bot className="h-5 w-5" />,
  },
  {
    path: '/admin/recharge',
    label: '充值中心',
    icon: <CreditCard className="h-5 w-5" />,
  },
  {
    path: '/admin/settings',
    label: '系统设置',
    icon: <Settings className="h-5 w-5" />,
  },
]

export const adminMenuGroups: MenuGroup[] = [
  {
    id: 'content',
    label: '账户与内容',
    items: [
      {
        path: '/admin/users',
        label: '用户管理',
        icon: <Users className="h-5 w-5" />,
      },
      {
        path: '/admin/game-accounts',
        label: '游戏账户管理',
        icon: <Gamepad2 className="h-5 w-5" />,
      },
      {
        path: '/admin/skins',
        label: '皮肤管理',
        icon: <Palette className="h-5 w-5" />,
      },
      {
        path: '/admin/blueprints',
        label: '蓝图管理',
        icon: <Blocks className="h-5 w-5" />,
      },
      {
        path: '/admin/announcements',
        label: '公告管理',
        icon: <MessageSquare className="h-5 w-5" />,
      },
      {
        path: '/admin/activities',
        label: '活动管理',
        icon: <Sparkles className="h-5 w-5" />,
      },
    ],
  },
  {
    id: 'reward',
    label: '奖励系统',
    items: [
      {
        path: '/admin/reward-packages',
        label: '奖励包管理',
        icon: <Gift className="h-5 w-5" />,
      },
      {
        path: '/admin/custom-reward-items',
        label: '自定义物品',
        icon: <PackageSearch className="h-5 w-5" />,
      },
      {
        path: '/admin/checkin-reward-rules',
        label: '签到奖励规则',
        icon: <CalendarRange className="h-5 w-5" />,
      },
    ],
  },
]

const adminMobileItems: MenuItem[] = [
  ...adminCoreMenuItems,
  ...adminMenuGroups.flatMap(group => group.items),
]

function isItemActive(pathname: string, path: string) {
  return path === '/admin' ? pathname === '/admin' : pathname.startsWith(path)
}

function getActiveAdminMenuItem(pathname: string) {
  return adminMobileItems.find(item => isItemActive(pathname, item.path)) ?? adminMobileItems[0]
}

function getAdminMenuBadge(path: string, pendingCount: number) {
  if (path === '/admin/bots' && pendingCount > 0) {
    return pendingCount
  }
  return 0
}

export function AdminSidebar({ className }: AdminSidebarProps) {
  const location = useLocation()
  const { user } = useAuthStore()
  const { data: pendingMembers = [] } = usePendingMembers()
  const pendingCount = pendingMembers.length
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      adminMenuGroups.map(group => [
        group.id,
        group.items.some(item => isItemActive(location.pathname, item.path)),
      ])
    )
  )

  useEffect(() => {
    setExpandedGroups(prev => {
      const next = { ...prev }
      let changed = false

      for (const group of adminMenuGroups) {
        const shouldExpand = group.items.some(item => isItemActive(location.pathname, item.path))
        if (shouldExpand && !next[group.id]) {
          next[group.id] = true
          changed = true
        }
      }

      return changed ? next : prev
    })
  }, [location.pathname])

  return (
    <aside className={cn('fixed left-4 top-20 z-30 h-[calc(100vh-6rem)] w-72', className)}>
      <div className="flex h-full flex-col">
        <div className="app-surface flex h-full flex-col overflow-hidden px-4 py-5 text-slate-600 dark:text-slate-300">
          <div className="rounded-[1.6rem] bg-gradient-to-br from-[#504ada] via-[#5f58eb] to-[#6b63f0] p-5 text-white shadow-[0_20px_40px_rgba(80,74,218,0.28)]">
            <div className="flex items-center gap-4">
              <img
                src={resolveMediaUrl(user?.avatar)}
                alt={user?.username || 'Admin'}
                className="h-16 w-16 rounded-[1.35rem] border border-white/20 bg-white/10 object-contain p-1.5 dark:border-white/10 dark:bg-slate-950/20"
                style={{ imageRendering: 'pixelated' }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-lg font-semibold">{user?.username || '管理中心'}</p>
                <p className="truncate text-sm text-white/70">管理员权限</p>
              </div>
              <Shield className="h-5 w-5 text-white/85" />
            </div>
            <div className="mt-4 rounded-[1.2rem] border border-white/15 bg-white/10 px-3 py-2 text-sm text-white/80 dark:border-white/10 dark:bg-slate-950/20">
              次梦领域 Control
            </div>
          </div>

          <nav className="flex-1 overflow-y-auto px-2 py-5">
            <ul className="space-y-1.5">
              {adminCoreMenuItems.map(item => {
                const isActive = isItemActive(location.pathname, item.path)

                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={cn(
                        'flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-medium transition-all',
                        isActive
                          ? 'bg-gradient-to-r from-[#504ada] to-[#615af0] text-white shadow-[0_14px_26px_rgba(80,74,218,0.24)]'
                          : 'text-slate-600 hover:bg-[#eef1f8]/85 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#222427]/85 dark:hover:text-white'
                      )}
                    >
                      {item.icon}
                      <span className="flex-1">{item.label}</span>
                      {item.path === '/admin/bots' && pendingCount > 0 ? (
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                            isActive
                              ? 'bg-white/20 text-white dark:bg-slate-950/25'
                              : 'bg-indigo-600 text-white dark:bg-indigo-500'
                          )}
                        >
                          {pendingCount > 99 ? '99+' : pendingCount}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                )
              })}
            </ul>

            <div className="mt-5 space-y-3">
              {adminMenuGroups.map(group => {
                const isExpanded = expandedGroups[group.id]
                const hasActiveItem = group.items.some(item => isItemActive(location.pathname, item.path))

                return (
                  <section key={group.id} className="rounded-[1.35rem] border border-border/70 bg-secondary/46 p-2">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedGroups(prev => ({ ...prev, [group.id]: !prev[group.id] }))
                      }
                      className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-foreground transition hover:bg-background/70"
                    >
                      <span className="flex-1">{group.label}</span>
                      {hasActiveItem ? (
                        <span className="rounded-full bg-indigo-500/12 px-2 py-0.5 text-[11px] font-semibold text-indigo-600 dark:text-indigo-300">
                          当前
                        </span>
                      ) : null}
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </button>

                    {isExpanded ? (
                      <ul className="mt-1 space-y-1">
                        {group.items.map(item => {
                          const isActive = isItemActive(location.pathname, item.path)

                          return (
                            <li key={item.path}>
                              <NavLink
                                to={item.path}
                                className={cn(
                                  'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all',
                                  isActive
                                    ? 'bg-gradient-to-r from-[#504ada] to-[#615af0] text-white shadow-[0_12px_24px_rgba(80,74,218,0.2)]'
                                    : 'text-slate-600 hover:bg-background/80 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-[#222427]/85 dark:hover:text-white'
                                )}
                              >
                                {item.icon}
                                <span className="flex-1">{item.label}</span>
                              </NavLink>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </section>
                )
              })}
            </div>
          </nav>

          <div className="app-card-soft mt-auto px-4 py-3">
            <p className="text-xs font-medium text-slate-700 dark:text-slate-200">次梦领域 管理系统</p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              代理节点、用户与内容统一管理
            </p>
          </div>
        </div>
      </div>
    </aside>
  )
}

export function AdminMobileNav({ className }: AdminSidebarProps) {
  const [menuOpen, setMenuOpen] = useState(false)
  const location = useLocation()
  const { user } = useAuthStore()
  const { data: pendingMembers = [] } = usePendingMembers()
  const pendingCount = pendingMembers.length
  const activeItem = getActiveAdminMenuItem(location.pathname)
  const activeBadge = getAdminMenuBadge(activeItem.path, pendingCount)

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.search])

  return (
    <div className={cn('2xl:hidden', className)}>
      <div className="sticky top-[4.5rem] z-20">
        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          className="app-card-soft grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 text-left"
        >
          <div className="flex min-w-0 items-center gap-3 overflow-hidden">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[1rem] border border-border/80 bg-secondary/85 text-foreground">
              {activeItem.icon}
            </div>
            <div className="min-w-0 flex-1 overflow-hidden">
              <p className="text-xs font-medium text-muted-foreground">当前页面</p>
              <p className="truncate text-sm font-semibold text-foreground">{activeItem.label}</p>
            </div>
          </div>
          <div className="flex min-w-0 shrink-0 items-center gap-1.5 sm:gap-2">
            {activeBadge > 0 ? (
              <span className="shrink-0 rounded-full bg-foreground px-2 py-0.5 text-[11px] font-semibold text-background dark:bg-slate-100 dark:text-slate-950">
                {activeBadge > 99 ? '99+' : activeBadge}
              </span>
            ) : null}
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-border/80 bg-card/88 px-2 py-1.5 text-sm font-medium text-foreground sm:px-3">
              <Menu className="h-4 w-4" />
              <span className="max-[380px]:hidden">目录</span>
            </span>
          </div>
        </button>
      </div>

      <BottomSheet open={menuOpen} onOpenChange={setMenuOpen}>
        <BottomSheetContent>
          <BottomSheetHeader>
            <BottomSheetHandle />
            <div className="flex items-center gap-3 pr-12">
              <img
                src={resolveMediaUrl(user?.avatar)}
                alt={user?.username || '管理中心'}
                className="h-11 w-11 rounded-[1rem] border border-border/80 bg-secondary/75 object-contain p-1"
                style={{ imageRendering: 'pixelated' }}
              />
              <div className="min-w-0">
                <BottomSheetTitle className="truncate text-left text-base font-semibold text-foreground">
                  {user?.username || '管理中心'}
                </BottomSheetTitle>
                <p className="mt-1 text-sm text-muted-foreground">选择要进入的管理页面</p>
              </div>
            </div>
          </BottomSheetHeader>

          <div className="max-h-[calc(min(85vh,42rem)-5.5rem)] overflow-y-auto px-4 pb-5 pt-4">
            <div className="space-y-2">
              {adminMobileItems.map(item => {
                const isActive = isItemActive(location.pathname, item.path)
                const badge = getAdminMenuBadge(item.path, pendingCount)

                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMenuOpen(false)}
                    className={cn(
                      'flex items-center gap-3 rounded-[1.2rem] border px-4 py-3 text-sm font-medium transition',
                      isActive
                        ? 'border-indigo-300 bg-indigo-50 text-indigo-700 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200'
                        : 'border-border/80 bg-card/82 text-foreground hover:bg-accent'
                    )}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.95rem] border border-border/70 bg-secondary/78">
                      {item.icon}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{item.label}</span>
                    {badge > 0 ? (
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                          isActive
                            ? 'bg-indigo-600 text-white dark:bg-indigo-400 dark:text-slate-950'
                            : 'bg-foreground text-background dark:bg-slate-100 dark:text-slate-950'
                        )}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    ) : null}
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </NavLink>
                )
              })}
            </div>
          </div>
        </BottomSheetContent>
      </BottomSheet>
    </div>
  )
}
